import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ViewLink } from "@/components/shared/ListChrome";
import api from "@/lib/api";
import { StackedList, Pagination } from "@/components/shared/StackedList";
import { LoadingState, ErrorState, EmptyState } from "@/components/shared/States";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const KINDS = [
  { value: "all", label: "Everyone" },
  { value: "staff", label: "Staff" },
  { value: "contact", label: "Client contacts" },
  { value: "guest", label: "Guests" },
  { value: "legacy_guest", label: "Legacy archive · guests" },
  { value: "legacy_contact", label: "Legacy archive · client contacts" },
];
const LEGACY_KINDS = ["legacy_guest", "legacy_contact"];
const kindLabel = (k) => (k === "legacy_guest" ? "guest · legacy" : k === "legacy_contact" ? "contact · legacy" : k);
const openPath = (p) => (p.kind === "guest" || p.kind === "legacy_guest" ? `/control-centre/people/guests/${p.id}` : p.kind === "legacy_contact" && p.client_id ? `/control-centre/clients/legacy/${p.client_id}` : null);

export default function People() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [q, setQRaw] = useState("");
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 25;
  const setQ = (v) => { setQRaw(v); setPage(1); }; // a new search always starts at page 1
  const [kind, setKind] = useState("all");

  const load = () => {
    const params = { page, page_size: PAGE_SIZE };
    if (q) params.q = q;
    if (kind !== "all") params.kind = kind;
    api.get("/people", { params }).then((r) => setData(r.data)).catch(() => setError(true));
  };

  useEffect(() => { load(); }, [q, kind, page]); // eslint-disable-line react-hooks/exhaustive-deps

  if (error) return <ErrorState testId="people-error" />;

  return (
    <div data-testid="people-page" className="space-y-5">
      <div>
        <h1 className="text-[24px] font-semibold leading-tight tracking-[-0.01em] text-jigsaw-navy">People</h1>
        <p className="text-sm text-jigsaw-muted">Search staff, client contacts and guests across Jigsaw Platform Alpha.</p>
      </div>

      <div className="flex flex-wrap gap-3">
        <Input data-testid="people-search-input" placeholder="Search by name or email..." value={q} onChange={(e) => setQ(e.target.value)} className="max-w-xs" />
        <Select value={kind} onValueChange={(v) => { setKind(v); setPage(1); }}>
          <SelectTrigger data-testid="people-kind-filter" className="w-48"><SelectValue /></SelectTrigger>
          <SelectContent>
            {KINDS.map((k) => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {data?.legacy_totals && (data.legacy_totals.legacy_guest > 0 || data.legacy_totals.legacy_contact > 0) && (
        <div data-testid="people-legacy-note" role="note" className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-[10px] border border-jigsaw-border bg-jigsaw-bg px-4 py-2.5 text-[13px] text-jigsaw-navy">
          <span className="rounded-full bg-jigsaw-navy px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-white">Legacy archive</span>
          <span>{LEGACY_KINDS.includes(kind) ? `${data.total.toLocaleString("en-GB")} matching historical record${data.total === 1 ? "" : "s"} (read-only, staff-only).` : "Historical records are listed separately:"}</span>
          <button data-testid="people-legacy-guests-link" type="button" aria-pressed={kind === "legacy_guest"} className={`underline decoration-jigsaw-orange underline-offset-2 ${kind === "legacy_guest" ? "font-semibold" : ""}`} onClick={() => { setKind("legacy_guest"); setPage(1); }}>{data.legacy_totals.legacy_guest.toLocaleString("en-GB")} guests</button>
          <button data-testid="people-legacy-contacts-link" type="button" aria-pressed={kind === "legacy_contact"} className={`underline decoration-jigsaw-orange underline-offset-2 ${kind === "legacy_contact" ? "font-semibold" : ""}`} onClick={() => { setKind("legacy_contact"); setPage(1); }}>{data.legacy_totals.legacy_contact.toLocaleString("en-GB")} client contacts</button>
          <span className="basis-full text-xs text-jigsaw-muted">{data.legacy_note}</span>
        </div>
      )}

      {!data ? (
        <LoadingState testId="people-loading" />
      ) : data.items.length === 0 ? (
        <EmptyState testId="people-empty" title="No people found" />
      ) : (
        <div>
          <StackedList testId="people-stacked" items={data.items} title={(p) => p.full_name || "Name not recorded"} onOpen={(p) => navigate(openPath(p))} canOpen={(p) => Boolean(openPath(p))} openLabel="Open record"
            fields={[{ label: "Kind", value: (p) => kindLabel(p.kind) }, { label: "Organisation", value: (p) => p.organisation_name }, { label: "Role", value: (p) => p.role }, { label: "E-mail", value: (p) => p.email, full: true }]} />
          <div className="hidden sm:block">
          <Table data-testid="people-table">
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Kind</TableHead>
                <TableHead>Organisation</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Email</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.map((p) => (
                <TableRow key={`${p.kind}-${p.id}`} data-testid={`people-row-${p.id}`}>
                  <TableCell className="font-medium text-jigsaw-navy">{p.full_name || <span className="font-normal text-jigsaw-muted">Name not recorded</span>}</TableCell>
                  <TableCell className="text-xs uppercase text-jigsaw-muted">{kindLabel(p.kind)}</TableCell>
                  <TableCell>{p.organisation_name || "—"}</TableCell>
                  <TableCell className="text-xs text-jigsaw-muted">{p.role}</TableCell>
                  <TableCell className="text-xs text-jigsaw-muted">{p.email || "—"}</TableCell>
                  <TableCell className="text-right">{openPath(p) && <ViewLink testId={`people-open-guest-${p.id}`} label={p.kind === "legacy_contact" ? "Open legacy client" : "Open profile"} onClick={() => navigate(openPath(p))} />}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
          <Pagination testId="people-pagination" page={data.page} pageSize={data.page_size} total={data.total} onPage={setPage} />
        </div>
      )}
    </div>
  );
}
